import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CircleHelp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { helpTopics, type HelpTopicId } from './topics';
import { helpTopicsDe } from './topics.de';

const ui = {
  en: { help: 'Help', close: 'Close', overview: 'How jd agentic works: overview with drawings →' },
  de: { help: 'Hilfe', close: 'Schliessen', overview: 'So funktioniert jd agentic: Überblick mit Zeichnungen →' },
};

/** "Help" in a page header: opens a short, plain-language explanation of the current view (English or German). */
export function HelpButton({ topic }: { topic: HelpTopicId }) {
  const [open, setOpen] = useState(false);
  const { i18n } = useTranslation();
  const de = i18n.language === 'de';
  const help = (de ? helpTopicsDe : helpTopics)[topic];
  const l = de ? ui.de : ui.en;

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} aria-label={`${l.help}: ${help.title}`}>
        <CircleHelp /> <span className="hidden sm:inline">{l.help}</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="top-[8%] flex max-h-[84vh] max-w-xl flex-col">
          <div className="flex items-start justify-between gap-4 border-b px-5 py-4">
            <div>
              <DialogTitle className="text-base">{help.title}</DialogTitle>
              <DialogDescription className="mt-1 text-sm text-muted-foreground">{help.intro}</DialogDescription>
            </div>
            <DialogClose asChild>
              <Button variant="ghost" size="sm" aria-label={l.close}>
                <X />
              </Button>
            </DialogClose>
          </div>
          <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4 text-sm leading-relaxed">
            {help.sections.map((s) => (
              <section key={s.heading}>
                <h3 className="mb-1 font-medium">{s.heading}</h3>
                {s.body?.map((p) => (
                  <p key={p} className="text-muted-foreground">
                    {p}
                  </p>
                ))}
                {s.items &&
                  (s.numbered ? (
                    <ol className="list-decimal space-y-1 pl-5 text-muted-foreground">
                      {s.items.map((i) => (
                        <li key={i}>{i}</li>
                      ))}
                    </ol>
                  ) : (
                    <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                      {s.items.map((i) => (
                        <li key={i}>{i}</li>
                      ))}
                    </ul>
                  ))}
              </section>
            ))}
          </div>
          <div className="border-t px-5 py-3 text-sm">
            <Link to="/help" className="text-accent hover:underline" onClick={() => setOpen(false)}>
              {l.overview}
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
